import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  makeStyles,
  Tree,
  TreeCheckedChangeData,
  TreeCheckedChangeEvent,
  TreeItem,
  TreeItemLayout,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import {
  EHeadingsSelectionMode,
  EHeadingSelectionLabel,
  getItemPathByItemType,
  TSelectHeadingDialogState,
  TVersion,
  toggleHeadingHelper,
  buildTreeHeading,
  getAllAncestors,
  getAllDescendants,
} from '@modules/documents';
import { ControlledTooltip, OptionSwatch, ViewDialogItemButton } from '@shared/components';
import { SECTION_AUTO_SELECTED_TOOLTIP, SELECT_HEADING_DIALOG_OPTIONS } from '@modules/documents/constants';
import { THeadingTreeNode } from '@modules/documents/types';
import classNames from 'classnames';
import { SignInContext } from '../../../users';
import { buildHref } from '@shared/utils';

type TProps = {
  state?: TSelectHeadingDialogState;
  setState: (state: TSelectHeadingDialogState | undefined) => void;
  multiSelect: boolean;
  label: EHeadingSelectionLabel;
};

const renderTree = (
  treeHeadings: THeadingTreeNode[],
  localAutoDisabledIds: ReadonlySet<string>,
  documentId: string,
  versionId: string,
  excludeVersionIdParam = false,
  treeItemClassName: string,
  opts?: { isH5RefEnabled?: boolean }
): React.JSX.Element[] =>
  treeHeadings.map((h) => {
    const isAutoDisabled = localAutoDisabledIds.has(h.id);
    const isH5Ref = h.level === 5 && opts?.isH5RefEnabled;

    return (
      <TreeItem key={h.id} itemType={h.children.length > 0 ? 'branch' : 'leaf'} value={h.id}>
        <div className="flex justify-between items-center">
          <ControlledTooltip
            content={SECTION_AUTO_SELECTED_TOOLTIP}
            relationship="inaccessible"
            enable={isAutoDisabled}
            positioning="before"
            withArrow
          >
            <TreeItemLayout className={classNames({ [treeItemClassName]: isAutoDisabled }, 'w-full')}>
              {!isH5Ref ? (
                <p className="cursor-default">
                  {h.numbering} {h.text}
                </p>
              ) : (
                <p className="cursor-default">{h.text}</p>
              )}
            </TreeItemLayout>
          </ControlledTooltip>
          <ViewDialogItemButton
            href={buildHref(`/${getItemPathByItemType('DOCUMENT')}/${documentId}`, {
              view: excludeVersionIdParam ? undefined : versionId,
              elem: h.id,
            })}
            tooltipContent={'View section in new tab'}
          />
        </div>
        {h.children.length > 0 && (
          <Tree>
            {renderTree(
              h.children,
              localAutoDisabledIds,
              documentId,
              versionId,
              excludeVersionIdParam,
              treeItemClassName,
              opts
            )}
          </Tree>
        )}
      </TreeItem>
    );
  });

const useStyles = makeStyles({
  dialogContent: {
    minHeight: '20vh',
    maxHeight: '60vh',
    overflowY: 'auto',
  },
  actions: {
    gridColumnStart: 1,
  },
  disabledTreeItem: {
    '& .fui-Checkbox': {
      pointerEvents: 'none',
      cursor: 'not-allowed',
      opacity: 0.5,
    },
  },
});

const SelectHeadingDialog = ({ state, setState, multiSelect, label }: TProps) => {
  const {
    organization: { mock },
  } = useContext(SignInContext);
  // Local state alternative to `selectedHeadingIds` and `autoDisabledIds` from the `useImpactSourceStore` zustand store
  const [localSelectedHeadingIds, setLocalSelectedHeadingIds] = useState<ReadonlySet<string>>(new Set());
  const [localAutoDisabledIds, setLocalAutoDisabledIds] = useState<ReadonlySet<string>>(new Set());

  // The user toggles a heading in the checkbox sense
  const handleCheckedChange = (_e: TreeCheckedChangeEvent, data: TreeCheckedChangeData) => {
    const toggledHeadingId = data.value as string;

    // If single-select, just select the one heading
    if (!multiSelect) {
      setLocalSelectedHeadingIds(new Set([toggledHeadingId]));
      return;
    }

    // Toggle it in the selection sets
    let newSelected = new Set(localSelectedHeadingIds);
    let newDisabled = new Set(localAutoDisabledIds);

    [newSelected, newDisabled] = toggleHeadingHelper(
      state!.version,
      toggledHeadingId,
      newSelected,
      newDisabled,
      state?.enableAutoChildrenSelection
    );

    setLocalSelectedHeadingIds(newSelected);
    setLocalAutoDisabledIds(newDisabled);

    // Avoid collapsing/expanding the subtree if auto-disabled
    if (!state?.enableAutoChildrenSelection) return;

    // Expand or collapse that entire subtree
    if (newSelected.has(toggledHeadingId)) {
      expandSubtree(toggledHeadingId);
    } else {
      collapseSubtree(toggledHeadingId);
    }
  };

  // Build the checkmark state for <Tree>
  const checkedItemsMap = useMemo(() => {
    const map = new Map<string, boolean>();
    (state?.version?.headings || []).forEach((h) => {
      map.set(h.id, localSelectedHeadingIds.has(h.id));
    });
    return map;
  }, [state?.version?.headings, localSelectedHeadingIds]);

  // The user clicks “OK” to confirm their picks
  const handleSelect = async () => {
    // Defaults for `ALL` mode: no selected IDs and no auto-disabled IDs.
    let newSelectedIds: ReadonlySet<string> = new Set();
    let newAutoDisabledIds: ReadonlySet<string> = new Set();

    // If not in `ALL` mode, we need to update the selected IDs and auto-disabled IDs.
    if (state?.mode !== EHeadingsSelectionMode.ALL) {
      if (multiSelect) {
        newSelectedIds = localSelectedHeadingIds;
        newAutoDisabledIds = localAutoDisabledIds;
      } else {
        // In case of single–selection, take only the first selected item.
        newSelectedIds =
          localSelectedHeadingIds.size > 0 ? new Set(Array.from(localSelectedHeadingIds).slice(0, 1)) : new Set();
      }
    }

    await state?.onSelect(state.mode, newSelectedIds, newAutoDisabledIds);

    // Clean up the local state
    setTimeout(() => {
      setLocalSelectedHeadingIds(new Set());
      setLocalAutoDisabledIds(new Set());
    }, 1000);
  };

  const [localTreeData, setLocalTreeData] = useState<{
    treeHeadings: THeadingTreeNode[];
    parentMap: Record<string, string | null>;
  }>({ treeHeadings: [], parentMap: {} });

  // Tracks whatever the user opens/closes manually via fluent-ui `<Tree>`
  const [openItemsSet, setOpenItemsSet] = useState<ReadonlySet<string>>(new Set());

  // Called when the user manually expands or collapses a node
  const handleOpenChange = (_e: any, data: any) => {
    setOpenItemsSet(data.openItems as ReadonlySet<string>);
  };

  // Expand all children of a heading
  const expandSubtree = (rootId: string) => {
    const subtree = getAllDescendants(rootId, localTreeData.treeHeadings);
    subtree.add(rootId);
    setOpenItemsSet((prev) => new Set([...prev, ...subtree]));
  };

  // Collapse all children of a heading
  const collapseSubtree = (rootId: string) => {
    const subtree = getAllDescendants(rootId, localTreeData.treeHeadings);
    subtree.add(rootId);
    setOpenItemsSet((prev) => new Set([...prev].filter((id) => !subtree.has(id))));
  };

  // Build the headings & parentMap once the dialog opens
  useEffect(() => {
    // If there’s no version or the dialog isn’t open, don’t do anything
    if (!state?.version || !state?.isOpen) return;

    const { treeHeadings, parentMap } = buildTreeHeading(state?.version.headings || []);
    setLocalTreeData({ treeHeadings, parentMap });

    // If there are no initial selected IDs, don’t do anything
    if (!state?.initialSelectedIds) return;

    // Set the initial selected and auto-disabled IDs
    setLocalSelectedHeadingIds(new Set(state.initialSelectedIds));
    // There might be initial selected, but no auto-disabled IDs
    if (state?.initialAutoDisabledIds) setLocalAutoDisabledIds(new Set(state.initialAutoDisabledIds));

    // By default, open the ancestors of any initially selected headings
    const newOpenItems = getAllAncestors(state.initialSelectedIds, parentMap);
    setOpenItemsSet(newOpenItems);
  }, [state?.version, state?.initialSelectedIds, state?.isOpen]);

  const showTree = state?.mode === EHeadingsSelectionMode.SPECIFIC;
  const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

  const classes = useStyles();

  return (
    <Dialog
      open={state?.isOpen || false}
      onOpenChange={(_, data) => {
        if (state) setState({ ...state, isOpen: data.open });
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            {multiSelect ? `Select section(s) to ${label}` : `Select section to ${label}`}
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <OptionSwatch
              selectedValue={state?.mode}
              setSelectedValue={(value) => setState(state && { ...state, mode: value as EHeadingsSelectionMode })}
              options={SELECT_HEADING_DIALOG_OPTIONS}
              modeLocked={state?.modeLocked}
              className="mb-4"
            />
            {showTree && (
              <Tree
                aria-label={`Select section(s) to ${label}`}
                selectionMode={multiSelect ? 'multiselect' : 'single'}
                checkedItems={checkedItemsMap}
                onCheckedChange={handleCheckedChange}
                openItems={openItemsSet}
                onOpenChange={handleOpenChange}
                className="mx-2"
              >
                {state &&
                  renderTree(
                    localTreeData.treeHeadings,
                    localAutoDisabledIds,
                    state.documentId,
                    (state.version as TVersion)?.id || 'draft',
                    state.excludeVersionIdParam,
                    classes.disabledTreeItem,
                    { isH5RefEnabled: mock.isH5RefEnabled }
                  )}
              </Tree>
            )}
          </DialogContent>
          <DialogActions className={classes.actions}>
            <Button
              appearance="primary"
              onClick={handleSelect}
              disabled={localSelectedHeadingIds.size === 0 && showTree}
            >
              {capitalize(label)} {!showTree ? 'document' : multiSelect ? 'section(s)' : 'section'}
            </Button>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Cancel</Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default SelectHeadingDialog;
